import { markRaw, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useModal, useSteppedModal, type ModalResult, type StepDefinition } from '@/apps/core'
import type { AssetSummary } from '@/api/assets/assets'
import type { CollectionSubtype, CollectionView } from '@/api/assets/collections'
import type { FileSelectionModalProps, FileSelectionResult } from '../types/fileSelector.types'
import type { WorkspaceView } from '@/api/assets/workspaces'

/** The workspace a rename or delete modal is about, as the workspace reads answer it. */
export type WorkspaceItem = WorkspaceView

/**
 * Asset item for modal props.
 */
export interface AssetItem {
  uid: string
  name: string
  type: string
  isFolder?: boolean
  thumbnailUrl?: string | null
  /** The pool's validation lock, as a library row carries it — `null` or absent for none. */
  lock?: AssetSummary['lock']
  [key: string]: unknown
}

/**
 * Subtitle item for download modal.
 */
export interface SubtitleItem {
  id: string
  language: string
  name: string
}

// ============================================================================
// Result Types
// ============================================================================

export interface CreateWorkspaceResult {
  uid: string
  name: string
}

export interface CreateFolderResult {
  uid: string
  name: string
}

export type CreateCollectionResult = Pick<CollectionView, 'uid' | 'name' | 'subtype' | 'workspace_id' | 'parent_id'>

export interface RenameResult {
  name: string
}

export interface MoveResult {
  destinationId: string | null
}

export interface ShareResult {
  shareId: string
}

export interface GenerateSubtitlesResult {
  subtitleUids: string[]
  languages: string[]
}

export interface ImportSubtitleResult {
  language: string
}

export interface GenerateChaptersResult {
  /** The model's proposal, as `POST /assets/{asset_id}/chapters/suggestions` answered it. */
  chapters: { start_ms: number; name: string }[]
  mode: 'replace' | 'append'
}

export interface AIWritingResult {
  contentType: 'titles' | 'description' | 'summary' | 'seo_keywords'
  value: string
}

/** What publishing a selection on a channel wrote, as the modal resolves it. */
export interface AddToChannelResult {
  websiteId: string
  /** How many publications the batch actually wrote. */
  created: number
  /** How many media it left out — already published there, or not publishable at all. */
  skipped: number
}

export interface AddRelationshipResult {
  assetId: string
  relationshipType: string
}

/**
 * Workflow data collected during the relationship creation wizard.
 */
export interface AddRelationshipWizardData {
  source?: { mode: 'upload' | 'library' }
  /**
   * `isPublic` offers what is linked as a download on this asset's share page and in its player,
   * whichever way it was added.
   */
  file?: { files?: File[]; assets?: AssetItem[]; isPublic?: boolean }
  /**
   * The asset the edge leaves and the ones it already has a `related` row to, which the library
   * picker shows disabled: an asset is not linked to itself, and an edge is not added twice.
   */
  context?: { assetId: string; linkedIds: string[] }
}

// ============================================================================
// Modal Width Constants
// ============================================================================

const MODAL_WIDTH = {
  CONFIRMATION: 400,
  FORM_SMALL: 450,
  FORM: 500,
  FORM_MEDIUM: 600,
  LARGE: 700,
  PREVIEW: 900,
  FILE_SELECTOR: 960,
} as const

// ============================================================================
// Composable
// ============================================================================

/**
 * Composable for asset-related modals.
 *
 * Centralizes all modal opens for the assets app, ensuring consistent
 * configurations (width, header visibility, icons) and typed return values.
 *
 * Must be called at the top of a setup function.
 *
 * @example
 * ```typescript
 * const { workspace, assets, files, subtitles, confirm, storage, search } = useAssetModals()
 *
 * // Create a folder
 * const result = await assets.createFolder({ workspaceId, parentId })
 * if (result.confirmed && result.data) {
 *   console.log('Created folder:', result.data.name)
 * }
 *
 * // Show delete confirmation
 * const confirmed = await confirm.delete({
 *   message: 'This action cannot be undone.',
 *   itemName: 'My Asset',
 * })
 * ```
 */
export function useAssetModals() {
  const { open } = useModal()
  const { openSteppedModal } = useSteppedModal()
  const { t } = useI18n()

  // ==========================================================================
  // Workspace Modals
  // ==========================================================================

  const workspace = {
    /**
     * Opens the modal to create a new workspace.
     *
     * @param params.compact - Only asks for a name, other settings are folded.
     * @param params.defaultName - Pre-fills the name field.
     * @param params.redirect - Set to false to stay put and handle the created
     *   workspace yourself (upload flows resume with it instead of navigating).
     */
    async create(
      params: { compact?: boolean; defaultName?: string; redirect?: boolean } = {},
    ): Promise<ModalResult<CreateWorkspaceResult>> {
      const { default: CreateSpaceModal } = await import('../modals/CreateSpaceModal.vue')

      return open<CreateWorkspaceResult>(
        t('assets.workspaces.createModal.title'),
        markRaw(CreateSpaceModal as Component),
        {
          width: params.compact ? MODAL_WIDTH.FORM : MODAL_WIDTH.FORM_MEDIUM,
          props: {
            compact: params.compact ?? false,
            defaultName: params.defaultName ?? '',
            redirect: params.redirect ?? true,
          },
        },
      )
    },

    /**
     * Opens the modal to rename/edit a workspace.
     */
    async rename(params: { workspace: WorkspaceItem }): Promise<ModalResult<void>> {
      const { default: RenameSpaceModal } = await import('../modals/RenameSpaceModal.vue')

      return open(
        t('assets.workspaces.renameModal.title'),
        markRaw(RenameSpaceModal as Component),
        {
          width: MODAL_WIDTH.FORM,
          props: { workspace: params.workspace },
        },
      )
    },

    /**
     * Opens the modal to delete a workspace.
     */
    async delete(params: { workspace: WorkspaceItem }): Promise<ModalResult<boolean>> {
      const { default: DeleteSpaceModal } = await import('../modals/DeleteSpaceModal.vue')

      return open(
        t('assets.workspaces.deleteModal.title'),
        markRaw(DeleteSpaceModal as Component),
        {
          width: MODAL_WIDTH.FORM_SMALL,
          hideHeader: true,
          props: { workspace: params.workspace },
        },
      )
    },

    /**
     * Opens the modal that seats a member of the organization in a workspace. A seat is granted
     * to somebody already in the organization; bringing in anyone else is an organization
     * invitation, which this workspace-level gesture does not send.
     */
    async invite(params: {
      subjectName?: string
      workspaceId: string
    }): Promise<ModalResult<void>> {
      const { default: WorkspaceInviteModal } = await import('../modals/WorkspaceInviteModal.vue')

      return open(
        t('assets.workspaces.members.invite'),
        markRaw(WorkspaceInviteModal as Component),
        {
          // The organization's invite modal's width, so both invitations look the same.
          width: 520,
          hideHeader: true,
          props: {
            subjectName: params.subjectName,
            workspaceId: params.workspaceId,
          },
        },
      )
    },
  }

  // ==========================================================================
  // Asset Modals
  // ==========================================================================

  const assets = {
    /**
     * Opens the modal to create a new asset (upload, folder, collection, etc.).
     */
    async createNew(params: {
      workspaceId?: string | null
      parentId?: string | null
      /** `thirdPartyUrl` opens straight onto the form an external video's address is pasted in. */
      initialView?: 'main' | 'thirdPartyUrl'
    }): Promise<ModalResult<void>> {
      const { default: CreateNewAssetModal } = await import('../modals/CreateNewAssetModal.vue')

      return open(
        t('assets.createNew.title'),
        markRaw(CreateNewAssetModal as Component),
        {
          width: MODAL_WIDTH.FORM_MEDIUM,
          props: {
            workspaceId: params.workspaceId ?? null,
            parentId: params.parentId,
            initialView: params.initialView ?? 'main',
          },
        },
      )
    },

    /**
     * Opens the modal to create a folder.
     */
    async createFolder(params: {
      workspaceId?: string | null
      parentId?: string | null
      selectedCount?: number
      onCreated?: (folder: CreateFolderResult) => void
    }): Promise<ModalResult<CreateFolderResult>> {
      const { default: CreateFolderModal } = await import('../modals/CreateFolderModal.vue')

      return open(
        t('assets.folder.createTitle'),
        markRaw(CreateFolderModal as Component),
        {
          width: MODAL_WIDTH.FORM,
          props: {
            workspaceId: params.workspaceId,
            parentId: params.parentId,
            selectedCount: params.selectedCount,
            onCreated: params.onCreated,
          },
        },
      )
    },

    /**
     * Opens the modal to create a collection.
     */
    async createCollection(params: {
      workspaceId?: string | null
      parentId?: string | null
      initialSubtype?: CollectionSubtype | null
      lockSubtype?: boolean
      selectedCount?: number
    }): Promise<ModalResult<CreateCollectionResult>> {
      const { default: CreateCollectionModal } = await import('../modals/CreateCollectionModal.vue')

      return open(
        t('assets.collection.createTitle'),
        markRaw(CreateCollectionModal as Component),
        {
          width: MODAL_WIDTH.FORM,
          props: {
            workspaceId: params.workspaceId,
            parentId: params.parentId,
            initialSubtype: params.initialSubtype ?? null,
            lockSubtype: params.lockSubtype ?? false,
            selectedCount: params.selectedCount,
          },
        },
      )
    },

    /**
     * Opens the modal to rename an asset.
     */
    async rename(params: { asset: AssetItem }): Promise<ModalResult<RenameResult>> {
      const { default: RenameAssetModal } = await import('../modals/RenameAssetModal.vue')

      return open(
        t('assets.rename.title'),
        markRaw(RenameAssetModal as Component),
        {
          width: MODAL_WIDTH.FORM,
          props: { asset: params.asset },
        },
      )
    },

    /**
     * Opens the modal to move assets to another folder.
     */
    async move(params: {
      assets: AssetItem[]
      currentFolderId?: string | null
      workspaceId?: string | null
    }): Promise<ModalResult<MoveResult>> {
      const { default: MoveAssetsModal } = await import('../modals/MoveAssetsModal.vue')

      return open(
        t('assets.move.title'),
        markRaw(MoveAssetsModal as Component),
        {
          width: MODAL_WIDTH.LARGE,
          subtitle: t('assets.move.subtitle'),
          props: {
            assets: params.assets,
            currentFolderId: params.currentFolderId,
            workspaceId: params.workspaceId,
          },
        },
      )
    },

    /**
     * Opens the modal to share assets.
     */
    async share(params: {
      assets: AssetItem[]
      workspaceId: string
    }): Promise<ModalResult<ShareResult>> {
      const { default: ShareAssetsModal } = await import('../modals/ShareAssetsModal.vue')

      return open(
        t('assets.share.title'),
        markRaw(ShareAssetsModal as Component),
        {
          width: MODAL_WIDTH.FILE_SELECTOR,
          hideHeader: true,
          noPadding: true,
          props: {
            assets: params.assets,
            workspaceId: params.workspaceId,
          },
        },
      )
    },

    /**
     * Opens the modal to confirm asset deletion.
     */
    async delete(params: {
      message: string
      itemName: string
      note?: string
      /** Channel names this media is published on, as answered by the server. */
      publicationLabels?: string[]
      isBulk?: boolean
      /** True when the server could not inventory the usages (≠ none found). */
      usagesUnavailable?: boolean
    }): Promise<ModalResult<boolean>> {
      const { default: DeleteConfirmationModal } = await import(
        '../modals/DeleteConfirmationModal.vue'
      )

      return open(
        t('assets.delete.title'),
        markRaw(DeleteConfirmationModal as Component),
        {
          width: MODAL_WIDTH.FORM,
          hideHeader: true,
          props: {
            message: params.message,
            itemName: params.itemName,
            note: params.note,
            publicationLabels: params.publicationLabels,
            isBulk: params.isBulk,
            usagesUnavailable: params.usagesUnavailable,
          },
        },
      )
    },

    /**
     * Opens the dialog to select which fields to copy from another asset.
     */
    async copyFrom(params: {
      sourceAsset: { name: string; thumbnailUrl?: string | null; type: string }
      sourceTitle: string
      sourceDescription: string | null
      sourceTags: Array<{ uid: string; name: string }>
      currentTagsCount: number
      metadataFieldCount: number
    }): Promise<ModalResult<{ title: boolean; description: boolean; tags: boolean; tagsMode: 'add' | 'replace'; metadata: boolean }>> {
      const { default: CopyFromAssetDialog } = await import('../modals/CopyFromAssetDialog.vue')

      return open(
        t('assets.copyFrom.dialogTitle'),
        markRaw(CopyFromAssetDialog as Component),
        {
          width: MODAL_WIDTH.FORM_MEDIUM,
          props: params,
        },
      )
    },

    /**
     * Opens the modal to preview an asset.
     */
    async preview(params: {
      asset: AssetItem
      showOpenDetails?: boolean
      startAt?: number
      /** An asset read in the pool that `asset` is a linked file of, to open it through. */
      viaAssetId?: string
    }): Promise<ModalResult<void>> {
      const { default: AssetPreviewModal } = await import('../modals/AssetPreviewModal.vue')

      return open(
        '',
        markRaw(AssetPreviewModal as Component),
        {
          width: MODAL_WIDTH.PREVIEW,
          maxWidth: '95vw',
          hideHeader: true,
          noPadding: true,
          props: {
            asset: params.asset,
            ...(params.showOpenDetails !== undefined && { showOpenDetails: params.showOpenDetails }),
            ...(params.startAt !== undefined && { startAt: params.startAt }),
            ...(params.viaAssetId !== undefined && { viaAssetId: params.viaAssetId }),
          },
        },
      )
    },
  }

  // ==========================================================================
  // File Modals
  // ==========================================================================

  const files = {
    /**
     * Opens the file selection modal.
     */
    async select(
      options: FileSelectionModalProps = {},
    ): Promise<FileSelectionResult> {
      const { default: FileSelectionModal } = await import('../modals/FileSelectionModal.vue')

      const defaultTitle = options.multiple
        ? t('assets.fileSelector.selectFiles')
        : t('assets.fileSelector.selectFile')

      const result = await open<FileSelectionResult['data']>(
        options.title || defaultTitle,
        markRaw(FileSelectionModal as Component),
        {
          width: MODAL_WIDTH.FILE_SELECTOR,
          maxWidth: '90vw',
          noPadding: true,
          props: options as unknown as Record<string, unknown>,
        },
      )

      return {
        confirmed: result.confirmed,
        data: result.data,
      }
    },

    /**
     * Opens the modal to capture a thumbnail at a specific timestamp.
     */
    async captureThumbnail(params: {
      assetId: string
      duration: number
    }): Promise<ModalResult<string | null>> {
      const { default: CaptureThumbnailModal } = await import('../modals/CaptureThumbnailModal.vue')

      return open(
        t('assets.overview.thumbnails.captureModal.title'),
        markRaw(CaptureThumbnailModal as Component),
        {
          width: MODAL_WIDTH.LARGE,
          props: {
            assetId: params.assetId,
            duration: params.duration,
          },
        },
      )
    },

    /**
     * Opens the stepped wizard to add a relationship between assets.
     * Supports uploading a new file or selecting from the library.
     */
    async addRelationship(
      context: NonNullable<AddRelationshipWizardData['context']>,
    ): Promise<ModalResult<AddRelationshipWizardData>> {
      const [
        { default: RelationshipSourceStep },
        { default: RelationshipFileStep },
      ] = await Promise.all([
        import('../modals/steps/RelationshipSourceStep.vue'),
        import('../modals/steps/RelationshipFileStep.vue'),
      ])

      const steps: StepDefinition[] = [
        {
          id: 'source',
          title: t('subtitles.wizard.source.title', 'Source'),
          component: markRaw(RelationshipSourceStep as Component),
          validation: (data: Record<string, unknown>) => {
            const sourceData = data.source as AddRelationshipWizardData['source']
            return sourceData?.mode
              ? true
              : t('assets.files.relationships.wizard.validation.selectSource')
          },
        },
        {
          id: 'file',
          title: t('assets.files.relationships.wizard.file.title'),
          component: markRaw(RelationshipFileStep as Component),
          validation: (data: Record<string, unknown>) => {
            const fileData = data.file as AddRelationshipWizardData['file']
            if (!fileData?.files?.length && !fileData?.assets?.length) {
              return t('assets.files.relationships.wizard.validation.selectFile')
            }
            return true
          },
        },
      ]

      return openSteppedModal<AddRelationshipWizardData>(
        t('assets.files.relationships.wizard.title'),
        steps,
        { maxWidth: MODAL_WIDTH.FORM_MEDIUM, initialData: { context } },
      )
    },
  }

  // ==========================================================================
  // Subtitle Modals
  // ==========================================================================

  const subtitles = {
    /**
     * Opens the modal to generate subtitles in one or more languages using AI.
     */
    async generate(params: {
      assetId: string
      existingLanguages: string[]
      /** Media duration in seconds — feeds the cost estimate shown in the modal. */
      durationSeconds?: number | null
    }): Promise<ModalResult<GenerateSubtitlesResult>> {
      const { default: AddLanguageModal } = await import(
        '@/apps/subtitles/modals/AddLanguageModal.vue'
      )

      return open(
        t('subtitles.addLanguage.title'),
        markRaw(AddLanguageModal as Component),
        {
          maxWidth: MODAL_WIDTH.FORM,
          noPadding: true,
          props: {
            assetId: params.assetId,
            existingLanguages: params.existingLanguages,
            durationSeconds: params.durationSeconds ?? null,
          },
        },
      )
    },

    /**
     * Opens the modal to select language before importing a subtitle file.
     */
    async importFile(params: {
      fileName: string
    }): Promise<ModalResult<ImportSubtitleResult>> {
      const { default: ImportSubtitleModal } = await import('../modals/ImportSubtitleModal.vue')

      return open(
        t('assets.subtitles.importModal.title'),
        markRaw(ImportSubtitleModal as Component),
        {
          width: MODAL_WIDTH.FORM_SMALL,
          hideHeader: true,
          props: { fileName: params.fileName },
        },
      )
    },

    /**
     * Opens the modal to download subtitles.
     */
    async download(params: {
      availableSubtitles: SubtitleItem[]
      mediaName?: string
      assetId: string
    }): Promise<ModalResult<void>> {
      const { default: DownloadSubtitlesModal } = await import(
        '@/apps/subtitles/modals/DownloadSubtitlesModal.vue'
      )

      return open(
        t('subtitles.download.title'),
        markRaw(DownloadSubtitlesModal as Component),
        {
          width: MODAL_WIDTH.FORM,
          hideHeader: true,
          props: {
            availableSubtitles: params.availableSubtitles,
            mediaName: params.mediaName,
            assetId: params.assetId,
          },
        },
      )
    },
  }

  // ==========================================================================
  // Chapter Modals
  // ==========================================================================

  const chapters = {
    /**
     * Opens the modal to generate chapters using AI.
     */
    async generate(params: { assetId: string }): Promise<ModalResult<GenerateChaptersResult>> {
      const { default: GenerateChaptersModal } = await import('../modals/GenerateChaptersModal.vue')

      return open(
        t('assets.chapters.generateModal.title'),
        markRaw(GenerateChaptersModal as Component),
        {
          width: MODAL_WIDTH.LARGE,
          hideHeader: true,
          persistent: true,
          props: { assetId: params.assetId },
        },
      )
    },
  }

  // ==========================================================================
  // AI Writing Modals
  // ==========================================================================

  const aiWriting = {
    /**
     * Opens the unified AI Writing modal (V2 — vertical card layout).
     */
    async openV2(params: {
      assetId: string
      assetName: string
      assetType: string
      thumbnailUrl?: string | null
      hasSpeech?: boolean | null
      initialContentType?: 'titles' | 'description' | 'summary' | 'seo_keywords'
      onApply?: (contentType: string, value: string) => void
    }): Promise<ModalResult<AIWritingResult>> {
      const { default: AIWritingModalV2 } = await import('../modals/AIWritingModalV2.vue')

      return open(
        '',
        markRaw(AIWritingModalV2 as Component),
        {
          width: '100%',
          maxWidth: '720px',
          hideHeader: true,
          noPadding: true,
          props: params,
        },
      )
    },
  }

  // ==========================================================================
  // Embed Modals
  // ==========================================================================

  const embed = {
    /**
     * Opens the embed code modal for an asset: a code built on `publicationId` — the asset's own
     * uid for an organization carrying `embeds.asset_direct`, else its native embed's — which the
     * callers resolve through `useEmbedPublication`.
     */
    async show(params: {
      assetId: string
      /** The uid the code names; the asset's own when absent. */
      publicationId?: string
      assetType: string
      assetName: string
      collectionSubtype?: string
      posterUrl?: string
      /**
       * `guided` pour l'onboarding (destination + affichage visibles), `compact`
       * par défaut pour le bouton « Intégrer » d'un média.
       */
      variant?: 'guided' | 'compact'
      /**
       * Called when the snippet reaches the clipboard.
       *
       * The modal container v-binds these props onto the component and closes
       * with `handleClose(modal.id)`, dropping any payload
       * (`ModalContainer.vue:59`), so a listener prop is the only channel that
       * survives: `onCodeCopied` is what `emit('code-copied')` reaches.
       */
      onCodeCopied?: () => void
    }): Promise<ModalResult<void>> {
      const { default: EmbedModal } = await import('../modals/EmbedModal.vue')

      return open(
        t('assets.embed.title'),
        markRaw(EmbedModal as Component),
        {
          width: MODAL_WIDTH.LARGE,
          noPadding: true,
          props: params,
        },
      )
    },
  }

  // ==========================================================================
  // Confirmation Modals
  // ==========================================================================

  const confirm = {
    /**
     * Opens a generic confirmation modal.
     */
    async show(params: {
      message: string
      title?: string
      confirmLabel?: string
      cancelLabel?: string
      confirmColor?: string
      icon?: string
      iconColor?: string
      persistent?: boolean
    }): Promise<ModalResult<boolean>> {
      const { default: ConfirmModal } = await import(
        '@/apps/core/components/feedback/ConfirmModal.vue'
      )

      return open(
        params.title || t('core.modals.confirm.title'),
        markRaw(ConfirmModal as Component),
        {
          width: MODAL_WIDTH.CONFIRMATION,
          hideHeader: true,
          persistent: params.persistent,
          props: {
            message: params.message,
            title: params.title,
            confirmLabel: params.confirmLabel,
            cancelLabel: params.cancelLabel,
            confirmColor: params.confirmColor,
            icon: params.icon,
            iconColor: params.iconColor,
          },
        },
      )
    },

    /**
     * Opens a delete confirmation modal with warning styling.
     */
    async delete(params: {
      message: string
      itemName?: string
      title?: string
      confirmLabel?: string
      cancelLabel?: string
    }): Promise<ModalResult<boolean>> {
      const { default: DeleteConfirmModal } = await import(
        '@/apps/core/components/feedback/DeleteConfirmModal.vue'
      )

      return open(
        params.title || t('core.modals.delete.title'),
        markRaw(DeleteConfirmModal as Component),
        {
          width: MODAL_WIDTH.CONFIRMATION,
          hideHeader: true,
          props: {
            message: params.message,
            itemName: params.itemName,
            title: params.title,
            confirmLabel: params.confirmLabel,
            cancelLabel: params.cancelLabel,
          },
        },
      )
    },
  }

  // ==========================================================================
  // Storage Modals
  // ==========================================================================

  const storage = {
    /**
     * Opens the storage details dialog.
     */
    async showDetails(): Promise<ModalResult<void>> {
      const { default: StorageDetailsModal } = await import(
        '../modals/StorageDetailsModal.vue'
      )

      return open(
        t('assets.storage.title'),
        markRaw(StorageDetailsModal as Component),
        {
          width: MODAL_WIDTH.FORM_MEDIUM,
          hideHeader: true,
        },
      )
    },
  }

  // ==========================================================================
  // Channel Modals
  // ==========================================================================

  const channel = {
    /**
     * Opens the modal that publishes a selection of the library on a channel: which channel, who
     * reaches it, and where it is filed. Resolves with what was written and what the backend
     * skipped — a media already published on that channel, or one it will not publish.
     *
     * The gesture itself is gated on `websites.catalogue.edit` by whoever offers it: that is the
     * key `website_items_*` checks, so the modal's own reads would answer 403 without it.
     */
    async addTo(params: {
      assetIds: string[]
    }): Promise<ModalResult<AddToChannelResult>> {
      const { default: AddToChannelModal } = await import('../modals/AddToChannelModal.vue')

      return open(
        t('assets.addToChannel.title'),
        markRaw(AddToChannelModal as Component),
        {
          width: MODAL_WIDTH.LARGE,
          props: { assetIds: params.assetIds },
        },
      )
    },
  }

  // ==========================================================================
  // Search Modals
  // ==========================================================================

  const search = {
    /**
     * Opens the modal to rename a saved search.
     */
    async rename(params: {
      currentName?: string
    }): Promise<ModalResult<string>> {
      const { default: RenameSearchModal } = await import('../modals/RenameSearchModal.vue')

      return open(
        t('assets.savedSearch.rename'),
        markRaw(RenameSearchModal as Component),
        {
          width: MODAL_WIDTH.FORM,
          props: { initialName: params.currentName || '' },
        },
      )
    },
  }

  return {
    workspace,
    assets,
    aiWriting,
    channel,
    embed,
    files,
    subtitles,
    chapters,
    confirm,
    storage,
    search,
  }
}
